import { DateTime } from "luxon";
import { classes } from "polytype";
import { invariant } from "react-intl/src/utils.js";
import { Article } from "../../article/index.js";
import assertObjectFound from "../../base/assertObjectFound.js";
import {
  AggregateMetaData,
  DataModel,
  ListDataModel,
  ListQueryModel,
  ReferenceModel,
} from "../../base/index.js";
import { config } from "../../config/config.js";
import { Contract, HostingConfiguration } from "../../contract/index.js";
import { Customer } from "../../customer/index.js";
import {
  File,
  type DomFile,
  type FileAccessTokenProvider,
} from "../../file/index.js";
import { StorageMetrics } from "../../monitoring/StorageMetrics";
import { Order } from "../../order/index.js";
import { ProjectListQuery } from "../../project/index.js";
import type { AsyncResourceVariant } from "../../react/index.js";
import { provideReact } from "../../react/index.js";
import { ServerAvatarAccessTokenProvider } from "./ServerAvatarAccessTokenProvider.js";
import type {
  ServerData,
  ServerDisableReason,
  ServerListItemData,
  ServerListQueryModelData,
  ServerStatus,
} from "./types.js";

export class Server extends ReferenceModel {
  public static aggregateMetaData = new AggregateMetaData(
    "project",
    "placementgroup",
  );
  public readonly fileAccessTokenProvider: FileAccessTokenProvider;
  public readonly projects: ProjectListQuery;

  public constructor(id: string) {
    super(id);
    this.fileAccessTokenProvider = new ServerAvatarAccessTokenProvider(this);
    this.projects = new ProjectListQuery({
      server: this,
    });
  }

  public static findAggregate(serverId?: string) {
    return serverId ? { id: serverId, ...Server.aggregateMetaData } : undefined;
  }

  public static ofId(id: string) {
    return new Server(id);
  }

  public static find = provideReact(async (id: string) => {
    const data = await config.behaviors.server.find(id);

    if (data !== undefined) {
      return new ServerDetailed(data);
    }
  });

  public static get = provideReact(async (id: string) => {
    const server = await this.find(id);
    assertObjectFound(server, "server", id);
    return server;
  });

  public static query(query: ServerListQueryModelData = {}) {
    return new ServerListQuery(query);
  }

  public findDetailed = provideReact(
    () => Server.find(this.id),
    [this.id],
  ) as AsyncResourceVariant<() => Promise<ServerDetailed | undefined>>;

  public getDetailed = provideReact(
    () => Server.get(this.id),
    [this.id],
  ) as AsyncResourceVariant<() => Promise<ServerDetailed>>;

  public getContract = provideReact(async () => {
    return await Contract.getByServer(this.id);
  }, [this.id]);

  public getHostingConfiguration = provideReact(async () => {
    const contract = await this.getContract();
    const article = await Article.get(
      contract.baseItemArticles[0]?.articleId ?? "",
    );

    return new HostingConfiguration(article, contract);
  }, [this.id]);

  public async updateDescription(description: string) {
    await config.behaviors.server.updateDescription(this.id, description);
  }

  public async uploadAvatar(file: DomFile) {
    await File.upload(file, this.fileAccessTokenProvider);
  }

  public getAvatarUploadRules = provideReact(async () => {
    return File.getUploadRules("avatar");
  });

  public async removeAvatar() {
    await config.behaviors.server.removeAvatar(this.id);
  }

  public async updateTariff(data: { storage: number; machineType?: string }) {
    const { storage, machineType } = data;

    invariant(!!machineType, "Machine type is required for tariff change");

    const contract = await this.getContract();

    await Order.createTariffChange({
      tariffChangeType: "server",
      tariffChangeData: {
        diskspaceInGiB: storage,
        machineType: machineType,
        contractId: contract.id,
      },
    });
  }

  public findStorageMetrics = provideReact(async () => {
    return await StorageMetrics.find(this.id, "server");
  }, [this.id]);

  public async updateStorageNotificationThreshold(threshold?: number) {
    await config.behaviors.server.updateStorageNotificationThreshold(
      this.id,
      threshold,
    );
  }
}

class ServerCommon extends classes(
  DataModel<ServerListItemData | ServerData>,
  Server,
) {
  public readonly customer: Customer;
  public readonly shortId: string;
  public readonly description: string;
  public readonly createdAt: DateTime;
  public readonly avatar?: File;
  public readonly storage: number;
  public readonly clusterName?: string;
  public readonly vcpu: number;
  public readonly ram: number;
  public readonly machineType: string;
  public readonly status: ServerStatus;
  public readonly disabledReason?: ServerDisableReason;

  public constructor(data: ServerListItemData | ServerData) {
    super([data], [data.id]);
    this.customer = Customer.ofId(data.customerId);
    this.shortId = data.shortId;
    this.vcpu = parseInt(data.machineType.cpu);
    this.ram = parseInt(data.machineType.memory.replace("Gi", ""));
    this.storage = parseInt(data.storage.replace("Gi", ""));
    this.description = data.description;
    this.clusterName = data.clusterName;
    this.createdAt = DateTime.fromISO(data.createdAt);
    this.avatar = data.imageRefId ? File.ofId(data.imageRefId) : undefined;
    this.machineType = data.machineType.name;
    this.status = data.status;
    this.disabledReason = data.disabledReason;
  }
}

export class ServerDetailed extends classes(
  ServerCommon,
  DataModel<ServerData>,
) {
  public constructor(data: ServerData) {
    super([data], [data]);
  }
}

export class ServerListItem extends classes(
  ServerCommon,
  DataModel<ServerListItemData>,
) {
  public constructor(data: ServerListItemData) {
    super([data], [data]);
  }
}

export class ServerListQuery extends ListQueryModel<ServerListQueryModelData> {
  public constructor(query: ServerListQueryModelData = {}) {
    super(query);
  }

  public refine(query: ServerListQueryModelData) {
    return new ServerListQuery({
      ...this.query,
      ...query,
    });
  }

  public execute = provideReact(async () => {
    const { customer, ...query } = this.query;
    const { items, totalCount } = await config.behaviors.server.list({
      limit: config.defaultPaginationLimit,
      customerId: customer?.id,
      ...query,
    });

    return new ServerList(
      this.query,
      items.map((d) => new ServerListItem(d)),
      totalCount,
    );
  }, [this.queryId, "server"]);

  public getTotalCount = provideReact(async () => {
    const { totalCount } = await this.refine({ limit: 1 }).execute();
    return totalCount;
  }, [this.queryId, "server"]);
}

export class ServerList extends classes(
  ServerListQuery,
  ListDataModel<ServerListItem>,
) {
  public constructor(
    query: ServerListQueryModelData,
    servers: ServerListItem[],
    totalCount: number,
  ) {
    super([query], [servers, totalCount]);
  }
}
